import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";

// ============================================
// CONSTANTS
// ============================================

const QUESTIONS = [
    "1. Little interest or pleasure in doing things",
    "2. Feeling down, depressed, or hopeless",
    "3. Trouble falling or staying asleep, or sleeping too much",
    "4. Feeling tired or having little energy",
    "5. Poor appetite or overeating",
    "6. Feeling bad about yourself — or that you are a failure or have let yourself or your family down",
    "7. Trouble concentrating on things, such as reading the newspaper or watching television",
    "8. Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual",
    "9. Thoughts that you would be better off dead or of hurting yourself in some way",
];

const VALUE_LABELS = {
    0: "Not at all",
    1: "Several days",
    2: "More than half the days",
    3: "Nearly every day",
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

const fmtDate = (val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
};

const firstNonEmpty = (...vals) => {
    for (const v of vals) {
        if (v === 0) return 0;
        if (v !== null && v !== undefined) {
            const s = String(v);
            if (s.trim() !== "") return v;
        }
    }
    return "";
};

const toBool = (v) => {
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v === 1;
    if (typeof v === "string") return ["true", "yes", "y", "1"].includes(v.trim().toLowerCase());
    return !!v;
};

const parseJsonDeep = (input, maxDepth = 5) => {
    let v = input;
    let depth = 0;
    try {
        while (typeof v === "string" && depth < maxDepth) {
            v = JSON.parse(v);
            depth++;
        }
    } catch {
        // Stop on parse errors
    }
    return v;
};

const getSeverity = (score) => {
    const s = Number(score) || 0;
    if (s >= 20) return "Severe depression";
    if (s >= 15) return "Moderately severe depression";
    if (s >= 10) return "Moderate depression";
    if (s >= 5) return "Mild depression";
    return "Minimal/none";
};

const getDifficultyFromFlags = (data) => {
    if (!data) return "—";
    if (toBool(data.ExtremelyDifficult) || toBool(data.extremelyDifficult)) return "Extremely difficult";
    if (toBool(data.VertDifficult) || toBool(data.vertDifficult) || toBool(data.VeryDifficult) || toBool(data.veryDifficult)) return "Very difficult";
    if (toBool(data.SomeWhatDifiicult) || toBool(data.someWhatDifiicult) || toBool(data.SomewhatDifficult) || toBool(data.somewhatDifficult)) return "Somewhat difficult";
    if (toBool(data.DifficultAll) || toBool(data.difficultAll) || toBool(data.NotDifficultAtAll) || toBool(data.notDifficultAtAll)) return "Not difficult at all";
    return "—";
};

// ============================================
// TABLE HELPER COMPONENTS
// ============================================

const Row = ({ label, value }) => (
    <tr>
        <td style={{ width: "50%", padding: "8px", border: "1px solid #ddd", fontWeight: 600 }}>
            {label}
        </td>
        <td style={{ width: "50%", padding: "8px", border: "1px solid #ddd", whiteSpace: "pre-wrap" }}>
            {value ?? "—"}
        </td>
    </tr>
);

const Section = ({ title }) => (
    <tr>
        <td colSpan={2} style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold" }}>
            {title}
        </td>
    </tr>
);

// ============================================
// MAIN COMPONENT
// ============================================

const PHQ9Preview = ({ data }) => {
    const [form, setForm] = useState({});
    const [phq9, setPhq9] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const { patientFormId, formTitle } = data || {};

    // ============================================
    // FETCH DATA
    // ============================================
    useEffect(() => {
        if (!patientFormId) {
            setLoading(false);
            setError("No Patient Form ID provided");
            return;
        }

        let active = true;

        const fetchData = async () => {
            setLoading(true);
            setError("");

            try {
                const token = localStorage.getItem("neuro_patientportal_auth_token");

                if (!token) {
                    throw new Error("Authentication token not found. Please log in.");
                }

                const headers = { Authorization: `Bearer ${token}` };

                const formUrl = `${process.env.REACT_APP_API_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`;
                const phq9Url = `${process.env.REACT_APP_API_URL}/Patients/GetPatientPHQ9ByFormId?patientFormId=${patientFormId}`;

                const [formResponse, phq9Response] = await Promise.all([
                    fetch(formUrl, { headers }),
                    fetch(phq9Url, { headers }),
                ]);

                if (!active) return;

                if (!formResponse.ok) {
                    throw new Error(`Form load failed (${formResponse.status})`);
                }

                const formRaw = await formResponse.json();
                const formData = Array.isArray(formRaw) ? (formRaw[0] || {}) : (formRaw || {});

                let phq9Data = {};
                if (phq9Response.ok) {
                    const phq9Text = await phq9Response.text();
                    if (phq9Text && phq9Text.trim() !== "" && phq9Text !== "null") {
                        phq9Data = parseJsonDeep(phq9Text) || {};
                    }
                }

                if (active) {
                    setForm(formData);
                    setPhq9(phq9Data);
                }

            } catch (err) {
                if (active) {
                    console.error("Error fetching data:", err);
                    setError(err.message || "Failed to load PHQ-9 Assessment.");
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            active = false;
        };
    }, [patientFormId]);

    // ============================================
    // LOADING STATE
    // ============================================
    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", height: "60vh", alignItems: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    // ============================================
    // ERROR STATE
    // ============================================
    if (error) {
        return (
            <Typography color="error" align="center" sx={{ p: 3 }}>
                {error}
            </Typography>
        );
    }

    // ============================================
    // PREPARE DATA FOR DISPLAY
    // ============================================

    const headerTitle = firstNonEmpty(formTitle) || "Patient Health Questionnaire (PHQ-9)";
    const dosVal = fmtDate(firstNonEmpty(phq9.DateOfService, phq9.dateOfService, form.assessmentDate));
    const fileNo = firstNonEmpty(form.accountNo, phq9.accountNo) || "—";
    const clientName = firstNonEmpty(form.patientName, phq9.ClientName, phq9.clientName) || "—";
    const dobVal = fmtDate(firstNonEmpty(phq9.DateOfBirth, phq9.dateOfBirth, form.dob));

    const answers = [
        parseInt(firstNonEmpty(phq9.Q1, phq9.q1), 10),
        parseInt(firstNonEmpty(phq9.Q2, phq9.q2), 10),
        parseInt(firstNonEmpty(phq9.Q3, phq9.q3), 10),
        parseInt(firstNonEmpty(phq9.Q4, phq9.q4), 10),
        parseInt(firstNonEmpty(phq9.Q5, phq9.q5), 10),
        parseInt(firstNonEmpty(phq9.Q6, phq9.q6), 10),
        parseInt(firstNonEmpty(phq9.Q7, phq9.q7), 10),
        parseInt(firstNonEmpty(phq9.Q8, phq9.q8), 10),
        parseInt(firstNonEmpty(phq9.Q9, phq9.q9), 10),
    ].map((v) => (Number.isNaN(v) ? -1 : v));

    const totalScore = firstNonEmpty(phq9.totalScore, phq9.TotalScore) ||
        answers.reduce((sum, a) => sum + (a >= 0 ? a : 0), 0);

    const severity = getSeverity(totalScore);
    const difficulty = getDifficultyFromFlags(phq9);
    const breakdown = answers.map((a) => (a >= 0 ? a : 0)).join(" + ") + ` = ${totalScore}`;

    // ============================================
    // RENDER
    // ============================================
    return (
        <Paper sx={{ p: 0, borderRadius: 2 }}>
            {/* Sticky Header */}
            <Box
                sx={{
                    position: "sticky",
                    top: "-16px",
                    zIndex: (t) => t.zIndex.appBar,
                    bgcolor: "#fff",
                    p: 4,
                    pb: 0,
                    mb: 3,
                }}
            >
                <Typography variant="subtitle2" sx={{ position: "absolute", top: 16, left: 16 }}>
                    <strong>DOS:</strong> {dosVal}
                </Typography>

                <Typography
                    variant="h6"
                    sx={{
                        position: "absolute",
                        top: 16,
                        left: "50%",
                        transform: "translateX(-50%)",
                        fontWeight: "bold",
                        textAlign: "center",
                    }}
                >
                    {headerTitle}
                </Typography>

                <Box sx={{ height: 28 }} />
                <Divider />
            </Box>

            {/* Patient strip */}
            <Box sx={{ px: 4 }}>
                <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                        <Typography variant="body2"><strong>File #:</strong> {fileNo}</Typography>
                    </Grid>
                    <Grid item xs={4} textAlign="center">
                        <Typography variant="body2"><strong>Client Name:</strong> {clientName}</Typography>
                    </Grid>
                    <Grid item xs={4} textAlign="right">
                        <Typography variant="body2"><strong>DOB:</strong> {dobVal}</Typography>
                    </Grid>
                </Grid>
                <Divider sx={{ mb: 3 }} />
            </Box>

            {/* Body */}
            <Box sx={{ px: 4, pb: 4 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Over the <strong>last 2 weeks</strong>, how often have you been bothered by{" "}
                    <strong>any</strong> of the following problems?
                </Typography>

                {/* Responses Table */}
                <TableContainer component={Paper} elevation={0} sx={{ mb: 3, border: "1px solid #ddd" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, width: "60%" }}>Question</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: "20%", textAlign: "center" }}>Score</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: "20%" }}>Response</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {QUESTIONS.map((question, index) => {
                                const answer = answers[index];
                                const responseLabel = answer >= 0 ? (VALUE_LABELS[answer] || `Score ${answer}`) : "—";
                                return (
                                    <TableRow key={index}>
                                        <TableCell>{question}</TableCell>
                                        <TableCell sx={{ textAlign: "center", fontWeight: 600 }}>
                                            {answer >= 0 ? answer : "—"}
                                        </TableCell>
                                        <TableCell>{responseLabel}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Score Summary */}
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12pt", marginBottom: "24px" }}>
                    <tbody>
                        <Section title="Score & Interpretation" />
                        <Row label="For Office Coding" value={breakdown} />
                        <Row label="Total Score" value={String(totalScore)} />
                        <Row label="Severity" value={severity} />
                    </tbody>
                </table>

                {/* Functional Difficulty */}
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12pt" }}>
                    <tbody>
                        <Section title="Functional Difficulty" />
                        <Row
                            label="If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?"
                            value={difficulty}
                        />
                    </tbody>
                </table>

                {/* Severity Guide */}
                <Box sx={{ mt: 4, p: 2, border: "1px solid #ddd", borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        PHQ-9 Severity Guide:
                    </Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Typography variant="body2">• 0-4: Minimal/none</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Typography variant="body2">• 5-9: Mild</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Typography variant="body2">• 10-14: Moderate</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Typography variant="body2">• 15-19: Moderately severe</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Typography variant="body2">• 20-27: Severe</Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Paper>
    );
};

export default PHQ9Preview;